import { useEffect, useMemo, useState } from 'react';
import { fetchXaiKey } from '@/lib/themeReference';
import { getReferencePages, type ThemeReferenceFileRecord } from '@/lib/themeReferenceFiles';
import { THEME_BUSINESS_CATEGORIES } from '@/lib/themeBusinessCategories';
import { generateThemeFromReference } from '@/lib/themeAiGeneration';

interface ThemeCategoryLite {
  id: number;
  titre: string;
}

export default function GenerationPanel({
  referenceFiles,
  selectedRefId,
  categories,
  userId,
  userIdcommerce,
  onGenerated,
}: {
  referenceFiles: ThemeReferenceFileRecord[];
  selectedRefId: number | null;
  categories: ThemeCategoryLite[];
  userId: number;
  userIdcommerce: string;
  onGenerated?: () => void;
}) {
  const [businessCategoryId, setBusinessCategoryId] = useState('mixed');
  const [themeTypeId, setThemeTypeId] = useState<string>('');
  const [themeName, setThemeName] = useState('');
  const [instructions, setInstructions] = useState(() => {
    try { return localStorage.getItem('theme-generator-instructions') || ''; } catch { return ''; }
  });
  const [hasApiKey, setHasApiKey] = useState<boolean | null>(null);

  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState('');
  const [savedInfo, setSavedInfo] = useState<{ themeId: number; pageCount: number } | null>(null);

  const selectedRef = useMemo(
    () => referenceFiles.find((r) => r.id === selectedRefId) || null,
    [referenceFiles, selectedRefId],
  );
  const selectedTypeName = useMemo(
    () => categories.find((c) => String(c.id) === themeTypeId)?.titre || '',
    [categories, themeTypeId],
  );

  const detectedPages = useMemo(
    () => (selectedRef ? getReferencePages(selectedRef.contenu) : []),
    [selectedRef],
  );

  useEffect(() => { fetchXaiKey().then((key) => setHasApiKey(!!key)); }, []);

  useEffect(() => {
    if (categories.length > 0 && !themeTypeId) setThemeTypeId(String(categories[0].id));
  }, [categories, themeTypeId]);

  useEffect(() => {
    if (selectedRef && !themeName) setThemeName(`${selectedRef.titre} — IA`);
  }, [selectedRef, themeName]);

  const handleGenerate = async () => {
    if (!selectedRef) return;
    setError('');
    setSavedInfo(null);
    setGenerating(true);
    setProgress('');
    try {
      const res = await generateThemeFromReference({
        reference: selectedRef,
        businessCategoryId,
        themeTypeName: selectedTypeName,
        themeName,
        instructions,
        userId,
        userIdcommerce,
        onProgress: setProgress,
      });
      setSavedInfo({ themeId: res.themeId, pageCount: res.pageCount });
      setProgress('');
      onGenerated?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
      setProgress('');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 mb-4">
        <i className="ri-robot-line text-foreground-500"></i>
        <h3 className="text-sm font-semibold text-foreground-900">Générer un nouveau thème</h3>
      </div>

      {/* Clé API */}
      <div className={`mb-4 px-3 py-2 rounded-md flex items-start gap-2 ${
        hasApiKey === null
          ? 'bg-background-100 border border-background-200/70'
          : hasApiKey
            ? 'bg-accent-50 border border-accent-100'
            : 'bg-amber-50 border border-amber-100'
      }`}>
        <i className={`text-sm flex-shrink-0 mt-0.5 ${
          hasApiKey === null
            ? 'ri-loader-4-line animate-spin text-foreground-400'
            : hasApiKey
              ? 'ri-check-line text-accent-500'
              : 'ri-alert-line text-amber-500'
        }`}></i>
        <span className="text-[11px] leading-relaxed ${
          hasApiKey === null
            ? 'text-foreground-500'
            : hasApiKey
              ? 'text-accent-800'
              : 'text-amber-800'
        }">
          {hasApiKey === null
            ? 'Vérification de la clé API Grok...'
            : hasApiKey
              ? 'Clé API xAI Grok configurée. La génération est prête.'
              : 'Aucune clé API xAI Grok configurée. Ajoute-la dans les paramètres de la plateforme.'}
        </span>
      </div>

      {/* Catégorie métier */}
      <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Catégorie (activité)</label>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {THEME_BUSINESS_CATEGORIES.map((bc) => (
          <button
            key={bc.id}
            type="button"
            onClick={() => setBusinessCategoryId(bc.id)}
            title={bc.description}
            className={`flex items-center gap-1.5 px-2.5 py-2 rounded-md text-[11px] font-medium text-left transition-colors cursor-pointer border ${
              businessCategoryId === bc.id
                ? 'bg-accent-100 border-accent-300 text-accent-800'
                : 'bg-background-50 border-background-200/70 text-foreground-600 hover:text-foreground-900'
            }`}
          >
            <i className={`${bc.icon} text-sm flex-shrink-0`}></i>
            <span className="truncate">{bc.shortLabel}</span>
          </button>
        ))}
      </div>

      {/* Type de thème */}
      <div className="mt-4">
        <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Type de thème</label>
        <select
          value={themeTypeId}
          onChange={(e) => setThemeTypeId(e.target.value)}
          className="w-full h-10 px-3 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 cursor-pointer"
        >
          <option value="">— Choisir un type —</option>
          {categories.map((c) => (
            <option key={c.id} value={String(c.id)}>{c.titre}</option>
          ))}
        </select>
      </div>

      {/* Nom */}
      <div className="mt-4">
        <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Nom du nouveau thème</label>
        <input
          type="text"
          value={themeName}
          onChange={(e) => setThemeName(e.target.value)}
          placeholder="Nom du thème généré"
          className="w-full h-10 px-3 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300"
        />
      </div>

      {/* Instructions */}
      <div className="mt-4">
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-xs font-semibold text-foreground-600">Directives de design (optionnel)</label>
          {instructions.trim() && (
            <button
              onClick={() => { setInstructions(''); try { localStorage.removeItem('theme-generator-instructions'); } catch { /* */ } }}
              className="text-[10px] text-red-500 hover:text-red-600 font-medium cursor-pointer whitespace-nowrap"
            >
              Effacer
            </button>
          )}
        </div>
        <textarea
          value={instructions}
          onChange={(e) => { setInstructions(e.target.value); try { localStorage.setItem('theme-generator-instructions', e.target.value); } catch { /* */ } }}
          placeholder={'Décris le design souhaité. Ces directives s\'ajoutent au fichier de référence, à la catégorie et au type de thème.\n\nEx : design minimaliste, accents terracotta, typographie serif premium'}
          rows={4}
          className="w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 resize-none"
        />
      </div>

      {/* Récap */}
      {selectedRef && (
        <div className="mt-3 px-3 py-2.5 rounded-md bg-background-100 border border-background-200/70">
          <div className="flex items-center gap-2 mb-1">
            <i className="ri-information-line text-foreground-400 text-xs"></i>
            <span className="text-[11px] font-semibold text-foreground-600">
              {detectedPages.length} page(s) seront générées
            </span>
          </div>
          <p className="text-[10px] text-foreground-500 leading-relaxed">
            {detectedPages.slice(0, 6).map((p) => p.label).join(' · ')}
            {detectedPages.length > 6 ? ` … +${detectedPages.length - 6}` : ''}
          </p>
        </div>
      )}

      {/* Bouton */}
      <button
        onClick={handleGenerate}
        disabled={generating || !selectedRef || hasApiKey === false}
        className="w-full h-11 mt-4 bg-foreground-950 text-background-50 rounded-full text-sm font-semibold whitespace-nowrap hover:bg-foreground-800 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {generating ? (
          <><i className="ri-loader-4-line animate-spin text-xs"></i>Génération en cours...</>
        ) : (
          <><i className="ri-magic-line"></i>Générer le thème</>
        )}
      </button>

      {generating && progress && (
        <div className="mt-3 flex items-center gap-2 px-3 py-2.5 rounded-md bg-background-100 border border-background-200/70">
          <i className="ri-loader-4-line animate-spin text-foreground-500"></i>
          <span className="text-xs text-foreground-600">{progress}</span>
        </div>
      )}

      {error && !generating && (
        <div className="mt-3 px-3 py-2.5 rounded-md bg-red-50 border border-red-100 flex items-start gap-2">
          <i className="ri-error-warning-line text-red-500 text-sm flex-shrink-0 mt-0.5"></i>
          <span className="text-xs text-red-700">{error}</span>
        </div>
      )}

      {savedInfo && !generating && (
        <div className="mt-4 px-3 py-3 rounded-md bg-accent-50 border border-accent-100">
          <div className="flex items-center gap-2 mb-1">
            <i className="ri-check-double-line text-accent-600"></i>
            <span className="text-xs font-semibold text-accent-800">Thème généré et enregistré !</span>
          </div>
          <p className="text-[11px] text-accent-700 leading-relaxed">
            {savedInfo.pageCount} page(s) avec contenu. Le thème est disponible dans la liste (brouillon). Ouvre-le depuis l&apos;éditeur pour l&apos;affiner.
          </p>
        </div>
      )}
    </div>
  );
}